import React, { useMemo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import SearchSelect from "components/ui/SearchSelect";
import AudienceSelect, {
  toAudienceValue,
  fromAudienceValue,
  audienceRules,
} from "components/products/AudienceSelect";
import { formatProductCategory } from "utils/helperFunctions";

// Package form — a bundle of one contractor company's products at one price.
// The scheduler sends every product in it to that company's people, so the
// product list only offers the chosen company's products.

const IN_HOUSE = "inHouse";

const errorBorderClass = (fieldError) => (fieldError ? "border-red-500" : "");

export default function PackageEdit({
  initialData = {},
  contractorCompanies = [],
  products = [],
  isSaving = false,
  onSubmit,
  onCancel,
}) {
  const isExisting = !!initialData?._id;

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData?.name || "",
      price: initialData?.price ?? "",
      contractorCompanyId: isExisting
        ? String(initialData?.contractorCompanyId?._id || initialData?.contractorCompanyId || IN_HOUSE)
        : "",
      productIds: (initialData?.productIds || []).map((p) => String(p?._id || p)),
      // Product id → hours, as saved.
      durations: { ...(initialData?.durations || {}) },
      audience: toAudienceValue(initialData?.offices),
    },
  });

  const contractorCompanyId = useWatch({ control, name: "contractorCompanyId" });
  const pickedProductIds = useWatch({ control, name: "productIds" });
  const productById = useMemo(
    () => new Map((products || []).map((p) => [String(p._id), p])),
    [products],
  );
  // A product's own shoot length, from its campaign event.
  const usualHoursOf = (id) =>
    productById.get(id)?.campaignEventId?.durationHours ?? null;

  const companyOptions = [
    { value: IN_HOUSE, label: "In-house" },
    ...(contractorCompanies || []).map((c) => ({
      value: String(c._id),
      label: c.name,
    })),
  ];

  const productOptions = useMemo(() => {
    if (!contractorCompanyId) return [];
    const companyId = contractorCompanyId === IN_HOUSE ? "" : contractorCompanyId;
    return (products || [])
      .filter(
        (p) => String(p.contractorCompanyId?._id || p.contractorCompanyId || "") === companyId,
      )
      .map((p) => ({
        value: String(p._id),
        label: p.name,
        subLabel: [
          formatProductCategory(p.category),
          p.price != null ? `$${p.price}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
      }));
  }, [products, contractorCompanyId]);

  const submit = (values) => {
    onSubmit({
      name: values.name.trim(),
      price: Number(values.price),
      ...(isExisting
        ? {}
        : {
            contractorCompanyId:
              values.contractorCompanyId === IN_HOUSE ? null : values.contractorCompanyId,
          }),
      productIds: values.productIds,
      // Only the picked products' times that differ from their usual length;
      // blank or the usual one keeps following the product.
      durations: Object.fromEntries(
        values.productIds
          .map((id) => [id, Number(values.durations?.[id])])
          .filter(
            ([id, hours]) => hours > 0 && hours !== usualHoursOf(id),
          ),
      ),
      offices: fromAudienceValue(values.audience),
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Package Name *
        </label>
        <input
          id="name"
          type="text"
          placeholder="e.g. Essentials"
          {...register("name", {
            required: "Package name is required",
            validate: (v) => !!v.trim() || "Package name is required",
          })}
          className={`form-input border ${errorBorderClass(errors.name)}`}
        />
        {errors.name && (
          <p className="form-error-message">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="price" className="form-label">
          Price *
        </label>
        <input
          id="price"
          type="number"
          step="0.01"
          min="0"
          placeholder="e.g. 650"
          {...register("price", {
            required: "Price is required",
            min: { value: 0, message: "Price must be 0 or more" },
          })}
          className={`form-input border ${errorBorderClass(errors.price)}`}
        />
        {errors.price ? (
          <p className="form-error-message">{errors.price.message}</p>
        ) : (
          <p className="text-sm text-darkgray">
            The bundle price, not the sum of the products.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label className="form-label">Contractor Company *</label>
        <Controller
          name="contractorCompanyId"
          control={control}
          rules={{ required: "Contractor company is required" }}
          render={({ field }) => (
            <SearchSelect
              options={companyOptions}
              value={companyOptions.find((o) => o.value === field.value) || null}
              onChange={(option) => {
                field.onChange(option?.value || "");
                // Products belong to the previous company — a package can
                // only hold one company's products.
                setValue("productIds", []);
              }}
              placeholder="Select a contractor company"
              disabled={isExisting}
              className={errorBorderClass(errors.contractorCompanyId)}
            />
          )}
        />
        {errors.contractorCompanyId ? (
          <p className="form-error-message">
            {errors.contractorCompanyId.message}
          </p>
        ) : (
          isExisting && (
            <p className="text-sm text-darkgray">
              A package can't be moved to another company.
            </p>
          )
        )}
      </div>

      <div className="space-y-2">
        <label className="form-label">Products *</label>
        <Controller
          name="productIds"
          control={control}
          rules={{
            validate: (v) => (v || []).length > 0 || "Select at least one product",
          }}
          render={({ field }) => (
            <SearchSelect
              options={productOptions}
              // Keep the picked order: the first product carries the bundle
              // price in the cart.
              value={(field.value || [])
                .map((id) => productOptions.find((o) => o.value === id))
                .filter(Boolean)}
              onChange={(opts) => field.onChange((opts || []).map((o) => o.value))}
              placeholder={
                !contractorCompanyId
                  ? "Select a contractor company first"
                  : productOptions.length
                    ? "Search products..."
                    : "This company has no products"
              }
              disabled={!productOptions.length}
              multiple
            />
          )}
        />
        {errors.productIds ? (
          <p className="form-error-message">{errors.productIds.message}</p>
        ) : (
          <p className="text-sm text-darkgray">
            The first product carries the bundle price in the cart.
          </p>
        )}
      </div>

      {(pickedProductIds || []).length > 0 && (
        <div className="space-y-2">
          <label className="form-label">Time in this package</label>
          <p className="text-sm text-darkgray">
            How long each product's shoot takes when it's booked in this package. It starts at
            the product's usual time; change it for this package only.
          </p>
          <Controller
            name="durations"
            control={control}
            render={({ field }) => (
              <div className="divide-y divide-mediumgray rounded-md border border-mediumgray">
                {(pickedProductIds || []).map((id) => {
                  const usual = usualHoursOf(id);
                  return (
                    <div key={id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="min-w-0 truncate text-sm text-darkergray">
                        {productById.get(id)?.name || "Product"}
                      </span>
                      <div className="flex flex-none items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="24"
                          step="0.25"
                          aria-label={`Hours for ${productById.get(id)?.name || "this product"}`}
                          placeholder={usual ? String(usual) : "—"}
                          // The package's time, else the product's own one filled in.
                          value={field.value?.[id] ?? usual ?? ""}
                          onChange={(e) =>
                            field.onChange({ ...field.value, [id]: e.target.value })
                          }
                          className="form-input w-24 border"
                        />
                        <span className="text-sm text-darkgray">hours</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          />
        </div>
      )}

      <div className="border-t border-mediumgray pt-4">
        <h5 className="font-medium mb-1">Available To</h5>
        <p className="text-sm text-darkgray mb-4">
          Only offices that use this contractor company see the package.
        </p>
        <Controller
          name="audience"
          control={control}
          rules={audienceRules}
          render={({ field }) => (
            <AudienceSelect
              value={field.value}
              onChange={field.onChange}
              error={errors.audience?.message}
            />
          )}
        />
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="gray-button"
          disabled={isSaving}
        >
          Cancel
        </button>
        <button type="submit" className="black-button" disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}
